import { useQuery } from '@apollo/client';
import { HirerOrderSummary } from '@seek/hirer-ecommerce';
import { useTranslations } from '@vocab/react';
import { Bleed, Notice, Text } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { CheckoutPageError } from 'src/app/shared/components/CheckoutPageError';
import { PaymentOption } from 'src/app/shared/constants/paymentOptions';
import {
  PAYMENT_TYPE_AMEX,
  SURCHARGE_TYPE_AMEX,
} from 'src/app/shared/constants/paymentType';
import { PRODUCT_SET_SELECTED_PRODUCT_PRICE_CHANGED } from 'src/app/shared/constants/productSetStatus';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { convertToCurrency } from 'src/app/utils/currency';
import { log } from 'src/app/utils/logging';
import { actionUpdatePaymentOption } from 'src/app/v1/state/modules/formData/paymentOption';
import { actionUpdateProductCurrency } from 'src/app/v1/state/modules/session/productCurrency';
import { actionUpdatePaymentMethod } from 'src/app/v1/state/modules/tealium/paymentMethod';
import { actionUpdateProductTotalPrice } from 'src/app/v1/state/modules/tealium/productTotalPrice';
import { useHubbleTrackEventHandler } from 'src/app/v1/views/hooks/useHubbleTrackEventHandler';
import { GET_CONTRACT_BALANCE } from 'src/graphql/contracts';
import { useGetOrderSummaryTotalQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';

interface Props {
  hidePaymentDetails?: boolean;
  handleHidePaymentDetails?: () => void;
  error?: Error | null;
  stackSpace?: React.ComponentProps<typeof Bleed>['top'];
}

export const OrderSummary = (props: Props) => {
  const {
    advertiserNation,
    cartOrderId,
    hidePrice,
    jobOrderId,
    paymentOption,
    paymentType,
    productDisplayName,
    productSetStatus,
    seekAdvertiserId,
  } = useSelector(
    (state: AppState) => ({
      adBudgetOrderId: state.session.adBudget?.orderId,
      advertiserNation: state.session.hirerAccount.nation,
      cartOrderId: state.session.cart.orderId,
      hidePrice: state.session.hirerAccount?.hidePrice,
      interactionId: state.session.interactionId,
      jobOrderId: state.session.jobOrderId,
      locationId: state.formData.jobLocationId,
      paymentOption: state.formData.paymentOption,
      paymentType: state.session.paymentType,
      productDisplayName: state.session.productDisplayName,
      productSetStatus: state.session.productSetStatus,
      roleTitle: state.formData.jobTitle,
      seekAdvertiserId: state.session.seekAdvertiserId,
      seekSubClassificationId: state.session.seekSubClassificationId,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);
  const { getHubbleTrackEventHandler } = useHubbleTrackEventHandler();
  const { getLocale } = useConfig();
  const [surchargeType, setSurchargeType] = useState<string>();

  // TODO: kill the jobOrderId after edit and refresh flow is moved flexible ad architecture
  const getOrderId = () => cartOrderId ?? jobOrderId ?? '';

  const { data: dataOrderSummaryTotal } = useGetOrderSummaryTotalQuery({
    variables: {
      orderId: getOrderId()!,
      advertiserId: seekAdvertiserId,
      advertiserNation,
    },
    skip: !getOrderId(),
  });

  const productPrice = dataOrderSummaryTotal?.orderSummary?.total.value;

  const productCurrency = dataOrderSummaryTotal?.orderSummary?.total.currency;

  useEffect(() => {
    dispatch(
      actionUpdateProductTotalPrice(
        productPrice ? convertToCurrency(productPrice).toString() : '0.00',
      ),
    );
    dispatch(actionUpdateProductCurrency(productCurrency));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPrice, productCurrency]);

  useEffect(() => {
    setSurchargeType(
      paymentOption === PaymentOption.CREDIT_CARD &&
        paymentType === PAYMENT_TYPE_AMEX
        ? SURCHARGE_TYPE_AMEX
        : undefined,
    );
  }, [paymentOption, paymentType]);

  const updatePaymentDetailsAndMethod = () => {
    // Set parent state variable to hide PaymentDetails panel if not already hidden
    if (!props.hidePaymentDetails && props.handleHidePaymentDetails) {
      props.handleHidePaymentDetails();
    }

    // Update payment method to invoice
    if (paymentOption !== PaymentOption.INVOICE) {
      dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE));
      dispatch(actionUpdatePaymentMethod(PaymentOption.INVOICE));
    }
  };

  const { error: contractBalanceError } = useQuery(GET_CONTRACT_BALANCE, {
    variables: {
      advertiserId: seekAdvertiserId,
      advertiserNation,
    },
    skip: !Boolean(advertiserNation) || !Boolean(seekAdvertiserId),
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    context: {
      headers: {
        source: 'checkout',
        site: advertiserNation,
      },
    },
    onCompleted: (data) => {
      if (data?.contractBalances.length > 0) {
        updatePaymentDetailsAndMethod();
      }
    },
  });

  if (contractBalanceError) {
    log.error(
      'OrderSummary | ContractBalance | Getting contract balance from graph failed | error',
      {
        error: contractBalanceError,
        seekAdvertiserId,
        advertiserNation,
        adProduct: productDisplayName,
      },
    );
  }

  const orderIsReady = getOrderId() && seekAdvertiserId;
  if (hidePrice || !orderIsReady) {
    return <Bleed top={props.stackSpace}>{null}</Bleed>;
  }

  return (
    <ErrorBoundary
      FallbackComponent={CheckoutPageError}
      onError={(error) => {
        log.error(
          'OrderSummary | error',
          {
            error,
            seekAdvertiserId,
            advertiserNation,
            adProduct: productDisplayName,
            orderId: getOrderId(),
            surchargeType,
          },
          true,
        );
      }}
    >
      {productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_PRICE_CHANGED && (
        <Notice tone="info">
          <Text>{t('price-changed-notice-text-content')}</Text>
        </Notice>
      )}

      <HirerOrderSummary
        advertiserId={seekAdvertiserId}
        advertiserNation={advertiserNation}
        orderId={getOrderId()}
        surchargeToApply={surchargeType}
        language={getLocale()} // passing locale as this language prop is typed to Locale
        styles={{
          padding: { paddingY: 'large', paddingX: 'gutter' },
          space: 'medium',
        }}
        logger={log}
        hubbleTrackEventHandler={getHubbleTrackEventHandler()}
      />
    </ErrorBoundary>
  );
};
